import React, { useState, useEffect, useRef } from "react";
import { FiEdit3, FiUpload, FiX } from "react-icons/fi";
import styles from "../../../styles/CompanyManagement/CompanyDetails.module.css";
import axiosInstance from "../../../utils/api";

const fields = [
  { label: "Tone of content", name: "ToneOfContent", type: "text" },
  { label: "Brand keywords", name: "BrandKeywords", type: "text" },
  { label: "Black listed words", name: "BlackListedWords", type: "text" },
  { label: "Content marketing goal", name: "ContentMarketingGoal", type: "text" },
  { label: "Brand fonts", name: "BrandFonts", type: "text" },
];

const colorFields = [
  { label: "Primary", name: "primaryColor" },
  { label: "Secondary", name: "secondaryColor" },
  { label: "Tertiary", name: "tertiaryColor" },
];

const BrandKit = ({ companyId }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!companyId) return;
    axiosInstance.get(`/api/v1/companies/${companyId}`)
      .then(res => {
        const brandKit = res.data.additional_infos?.brand_kit || {};
        // Get logo URL from company.logo.url (top level) or fallback to brand_kit.logo
        const logoUrl = res.data.logo?.url || brandKit.logo || "";
        setFormData({
          ...fields.reduce((acc, f) => ({ ...acc, [f.name]: brandKit[f.name] || "" }), {}),
          ...colorFields.reduce((acc, f) => ({ ...acc, [f.name]: brandKit[f.name] || "" }), {}),
          logo: logoUrl
        });
        if (logoUrl) {
          setLogoPreview(logoUrl);
        }
      });
  }, [companyId]);

  const handleEditClick = () => setIsEditing(true);
  const handleSaveClick = async () => {
    if (!companyId) return;
    
    // Fetch the latest additional_infos to avoid overwriting other sections
    const res = await axiosInstance.get(`/api/v1/companies/${companyId}`);
    const existing = res.data.additional_infos || {};
    const brandKitData = {
      ...fields.reduce((acc, f) => ({ ...acc, [f.name]: formData[f.name] || "" }), {}),
      ...colorFields.reduce((acc, f) => ({ ...acc, [f.name]: formData[f.name] || "" }), {})
    };

    const token = localStorage.getItem("token");

    // If there's a new logo file, send as FormData
    if (logoFile) {
      const patchFormData = new FormData();
      
      // Append logo file at company level (not in brand_kit)
      patchFormData.append("company[logo]", logoFile);
      
      // Append brand_kit fields (without logo)
      Object.keys(brandKitData).forEach(key => {
        patchFormData.append(`company[additional_infos][brand_kit][${key}]`, brandKitData[key] || "");
      });
      
      // Append other additional_infos sections to preserve them
      Object.keys(existing).forEach(key => {
        if (key !== 'brand_kit') {
          if (typeof existing[key] === 'object' && existing[key] !== null) {
            Object.keys(existing[key]).forEach(subKey => {
              patchFormData.append(`company[additional_infos][${key}][${subKey}]`, existing[key][subKey] || "");
            });
          } else {
            patchFormData.append(`company[additional_infos][${key}]`, existing[key] || "");
          }
        }
      });

      await axiosInstance.patch(`/api/v1/companies/${companyId}`, patchFormData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: token,
        },
      });
    } else {
      // No new logo file, send as JSON
      await axiosInstance.patch(`/api/v1/companies/${companyId}`, {
        company: {
          additional_infos: {
            ...existing,
            brand_kit: brandKitData
          }
        }
      }, {
        headers: {
          Authorization: token,
        },
      });
    }
    
    setIsEditing(false);
    setLogoFile(null); // Clear the file after successful save
  };
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Image size should be less than 10MB');
      return;
    }

    // Store the file to upload when saving
    setLogoFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setFormData({ ...formData, logo: "" });
    setLogoPreview(null);
    setLogoFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleLogoAreaClick = () => {
    if (isEditing && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div>
      <div className={styles.detailsHeader}>
        <span className={styles.detailsHeading}>Brand kit</span>
        {!isEditing ? (
          <button className={styles.editBtn} onClick={handleEditClick}>
            <FiEdit3 /> Edit
          </button>
        ) : (
          <button className={styles.saveBtn} onClick={handleSaveClick}>
            Save
          </button>
        )}
      </div>
      <div className={styles.separator} />
      <div className={styles.detailsContent}>
        {/* Logo Upload Section */}
        <div className={styles.detailItem}>
          <span className={styles.detailLabel}>Brand logo</span>
          {isEditing ? (
            <div className={styles.logoUploadArea}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                style={{ display: 'none' }}
              />
              {logoPreview ? (
                <div className={styles.logoPreviewContainer}>
                  <img 
                    src={logoPreview} 
                    alt="Brand Logo Preview" 
                    className={styles.logoPreviewImage}
                    onClick={handleLogoAreaClick}
                    style={{ cursor: 'pointer' }}
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveLogo();
                    }}
                    className={styles.logoRemoveBtn}
                    title="Remove logo"
                  >
                    <FiX size={14} />
                  </button>
                </div>
              ) : (
                <div
                  className={styles.logoUploadPlaceholder}
                  onClick={handleLogoAreaClick}
                  style={{ cursor: 'pointer' }}
                >
                  <div>
                    <FiUpload size={32} />
                    <p>Click to upload logo</p>
                    <span>PNG, JPG up to 10MB</span>
                  </div>
                </div>
              )}
            </div>
          ) : formData.logo ? (
            <div className={styles.logoBox}>
              <img src={formData.logo} alt="Brand Logo" className={styles.logoImage} />
            </div>
          ) : (
            <p>No logo uploaded</p>
          )}
        </div>

        {fields.map((field) => (
          <div className={styles.detailItem} key={field.name}>
            <span className={styles.detailLabel}>{field.label}</span>
            {isEditing ? (
              <input
                type={field.type}
                name={field.name}
                value={formData[field.name] || ""}
                onChange={handleChange}
              />
            ) : (
              <p>{formData[field.name]}</p>
            )}
          </div>
        ))}
        <div className={styles.brandColors}>
          <span className={styles.detailLabel}>Brand colours</span>
          <div className={styles.colorContainer}>
            {colorFields.map((color, index) => (
              <div className={styles.colorBox} key={color.name}>
                <div
                  className={styles.color}
                  style={{ backgroundColor: formData[color.name] || "#fff" }}
                ></div>
                <p>{color.label}</p>
                {isEditing ? (
                  <input
                    type="color"
                    name={color.name}
                    value={formData[color.name] || "#ffffff"}
                    onChange={handleChange}
                  />
                ) : (
                  <p>{formData[color.name]}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandKit;